import { PTOType, PTOTypeBalance } from '@/types/api';
import * as Styles from './TimeOffBalanceSlider.styles';

type Props = {
  balance: Partial<Record<PTOType, PTOTypeBalance>>;
  className?: string;
  onCardClick?: (type: PTOType) => void;
  isLoading?: boolean;
  hideScrollOnLoading?: boolean;
};

const chooseCaregiverLeave = (
  balance: Partial<Record<PTOType, PTOTypeBalance>>
): [PTOType, PTOTypeBalance | undefined] => {
  const primaryBalance = balance[PTOType.PRIMARY_CAREGIVER];
  const secondaryBalance = balance[PTOType.SECONDARY_CAREGIVER];

  if (!primaryBalance) return [PTOType.SECONDARY_CAREGIVER, secondaryBalance];
  if (!secondaryBalance) return [PTOType.PRIMARY_CAREGIVER, primaryBalance];
  if (primaryBalance && secondaryBalance) {
    if (
      primaryBalance.usedHours > secondaryBalance.usedHours ||
      primaryBalance.scheduledHours > secondaryBalance.scheduledHours
    ) {
      return [PTOType.PRIMARY_CAREGIVER, primaryBalance];
    }
    if (
      primaryBalance.usedHours <= secondaryBalance.usedHours ||
      primaryBalance.scheduledHours < secondaryBalance.scheduledHours
    ) {
      return [PTOType.SECONDARY_CAREGIVER, secondaryBalance];
    }
  }

  return [PTOType.SECONDARY_CAREGIVER, secondaryBalance];
};

const TimeOffBalanceSlider = ({
  balance,
  className,
  onCardClick,
  isLoading = false,
  hideScrollOnLoading = false,
}: Props): JSX.Element => {
  const [, caregiverBalance] = chooseCaregiverLeave(balance);

  return (
    <Styles.ButtonCards className={className} $hideScroll={hideScrollOnLoading && isLoading}>
      {[
        PTOType.PERSONAL_PAUSE,
        PTOType.HEALTH_PAUSE,
        PTOType.HEALTH_LEAVE,
        PTOType.PRIMARY_CAREGIVER,
        PTOType.GRIEF_PAUSE,
      ].map((type) => {
        const isCaregiverLeave =
          type === PTOType.PRIMARY_CAREGIVER || type === PTOType.SECONDARY_CAREGIVER;
        const customTitle = (isCaregiverLeave && 'Caregiver Leave') || undefined;
        const typeBalance = isCaregiverLeave ? caregiverBalance : balance[type];

        return (
          <Styles.Card
            type={type}
            key={type}
            title={customTitle}
            mainHours={
              (typeBalance?.unlimited ? typeBalance?.usedHours : typeBalance?.availableHours) || 0
            }
            scheduledHours={typeBalance?.scheduledHours || 0}
            isUnlimited={typeBalance?.unlimited}
            onClick={onCardClick ? () => onCardClick?.(type) : undefined}
            isLoading={isLoading}
          />
        );
      })}
    </Styles.ButtonCards>
  );
};

export default TimeOffBalanceSlider;
